"use client";

import { c as _c } from "react/compiler-runtime";
import { $convertFromMarkdownString, type Transformer } from "@lexical/markdown";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { $createParagraphNode, $createTextNode, $getRoot, $isElementNode, COMMAND_PRIORITY_HIGH, KEY_ENTER_COMMAND, KEY_ESCAPE_COMMAND, type EditorState, type LexicalNode, type RootNode, type TextNode } from "lexical";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { $isCodeNode, CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import ExampleTheme from "@/components/drops/create/lexical/ExampleTheme";
import { EmojiNode } from "@/components/drops/create/lexical/nodes/EmojiNode";
import { HashtagNode } from "@/components/drops/create/lexical/nodes/HashtagNode";
import { $createWaveMentionNode, WaveMentionNode } from "@/components/drops/create/lexical/nodes/WaveMentionNode";
import { $createMentionNode, MentionNode } from "@/components/drops/create/lexical/nodes/MentionNode";
import { GroupMentionNode } from "@/components/drops/create/lexical/nodes/GroupMentionNode";
import EmojiPlugin from "@/components/drops/create/lexical/plugins/emoji/EmojiPlugin";
import type { NewMentionsPluginHandles } from "@/components/drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewMentionsPlugin from "@/components/drops/create/lexical/plugins/mentions/MentionsPlugin";
import type { NewWaveMentionsPluginHandles } from "@/components/drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import NewWaveMentionsPlugin from "@/components/drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
import { HASHTAG_TRANSFORMER } from "@/components/drops/create/lexical/transformers/HastagTransformer";
import { SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import { MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/MentionTransformer";
import { GROUP_MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/GroupMentionTransformer";
import { getMentionedGroupsFromEditorState } from "@/components/drops/create/lexical/utils/groupMentionDetection";
import { WAVE_MENTION_TRANSFORMER } from "@/components/drops/create/lexical/transformers/WaveMentionTransformer";
import type { MentionedUser, MentionedWave } from "@/entities/IDrop";
import { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import CreateDropEmojiPicker from "../CreateDropEmojiPicker";
import { addBlankLinePlaceholders, removeBlankLinePlaceholders } from "./blankLinePlaceholders";
import { exportDropMarkdown, normalizeDropMarkdown } from "./normalizeDropMarkdown";
import { areMentionedGroupsEqual } from "@/helpers/waves/drop-group-mentions";
interface EditDropLexicalProps {
  readonly initialContent: string;
  readonly initialMentions: ApiDropMentionedUser[];
  readonly initialGroupMentions: ApiDropGroupMention[];
  readonly initialWaveMentions: ApiMentionedWave[];
  readonly canMentionAll: boolean;
  readonly waveId: string | null;
  readonly isSaving: boolean;
  readonly onSave: (content: string, mentions: ApiDropMentionedUser[], mentionedGroups: ApiDropGroupMention[], mentionedWaves: ApiMentionedWave[]) => void;
  readonly onCancel: () => void;
}
const MAX_MENTION_RECONSTRUCTION_PASSES = 20;
const BASE_EDIT_MARKDOWN_TRANSFORMERS = [...SAFE_MARKDOWN_TRANSFORMERS_WITHOUT_CODE, MENTION_TRANSFORMER, HASHTAG_TRANSFORMER, WAVE_MENTION_TRANSFORMER];
const convertCodeNodesToFences = (root: RootNode) => {
  const stack: LexicalNode[] = [...root.getChildren()];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    if ($isCodeNode(node)) {
      const language = node.getLanguage() ?? "";
      const safeLanguage = language.trim().replaceAll(/[`\n\r]/g, "");
      const codeText = node.getTextContent();
      const normalizedCode = codeText.endsWith("\n") ? codeText : `${codeText}\n`;
      const maxExistingFence = (codeText.match(/`+/g) ?? []).reduce((max, match) => Math.max(max, match.length), 0);
      const fenceLength = Math.max(3, maxExistingFence + 1);
      const fence = "`".repeat(fenceLength);
      const openFence = safeLanguage ? `${fence}${safeLanguage}` : fence;
      const fencedMarkdown = `${openFence}\n${normalizedCode}${fence}`;
      const paragraph = $createParagraphNode();
      paragraph.append($createTextNode(fencedMarkdown));
      node.replace(paragraph);
      continue;
    }
    if ($isElementNode(node)) {
      stack.push(...node.getChildren());
    }
  }
};
function reconstructSplitMention(currentNode: any, nextNode: any, mentionStart: RegExpMatchArray, mentionEnd: RegExpMatchArray) {
  const fullMention = mentionStart[0] + mentionEnd[0];
  const mentionRegex = /@\[(\w+)\]/;
  const mentionMatch = mentionRegex.exec(fullMention);
  if (!mentionMatch) return false;
  const handle = mentionMatch[1];
  if (!handle) return false;
  const mentionNode = $createMentionNode(`@${handle}`);
  const currentText = currentNode.getTextContent();
  const nextText = nextNode.getTextContent();
  const beforeMention = currentText.substring(0, currentText.length - mentionStart[0].length);
  const afterMention = nextText.substring(mentionEnd[0].length);
  if (beforeMention) {
    currentNode.setTextContent(beforeMention);
    currentNode.insertAfter(mentionNode);
  } else {
    currentNode.remove();
    nextNode.insertBefore(mentionNode);
  }
  if (afterMention) {
    nextNode.setTextContent(afterMention);
  } else {
    nextNode.remove();
  }
  return true;
}
function reconstructSplitWaveMention(currentNode: any, nextNode: any, mentionStart: RegExpMatchArray, mentionEnd: RegExpMatchArray) {
  const fullMention = mentionStart[0] + mentionEnd[0];
  const mentionRegex = /#\[([^\]]+)\]/;
  const mentionMatch = mentionRegex.exec(fullMention);
  if (!mentionMatch) return false;
  const waveName = mentionMatch[1];
  const mentionNode = $createWaveMentionNode(`#${waveName}`);
  const currentText = currentNode.getTextContent();
  const nextText = nextNode.getTextContent();
  const beforeMention = currentText.substring(0, currentText.length - mentionStart[0].length);
  const afterMention = nextText.substring(mentionEnd[0].length);
  if (beforeMention) {
    currentNode.setTextContent(beforeMention);
    currentNode.insertAfter(mentionNode);
  } else {
    currentNode.remove();
    nextNode.insertBefore(mentionNode);
  }
  if (afterMention) {
    nextNode.setTextContent(afterMention);
  } else {
    nextNode.remove();
  }
  return true;
}
function processSplitMentions(textNodes: Array<TextNode>): boolean {
  for (let i = 0; i < textNodes.length - 1; i++) {
    const currentNode = textNodes[i];
    const nextNode = textNodes[i + 1];
    const currentText = currentNode?.getTextContent();
    const nextText = nextNode?.getTextContent();
    const mentionStart = currentText?.match(/@\[\w*$/);
    const mentionEnd = nextText?.match(/^\w*\]/);
    if (mentionStart && mentionEnd) {
      try {
        if (reconstructSplitMention(currentNode, nextNode, mentionStart, mentionEnd)) {
          return true;
        }
      } catch (error) {
        console.warn("Failed to reconstruct split mention", error);
      }
    }
    const waveMentionStart = currentText?.match(/#\[[^\]]*$/);
    const waveMentionEnd = nextText?.match(/^[^\]]*\]/);
    if (waveMentionStart && waveMentionEnd) {
      try {
        if (reconstructSplitWaveMention(currentNode, nextNode, waveMentionStart, waveMentionEnd)) {
          return true;
        }
      } catch (error) {
        console.warn("Failed to reconstruct split wave mention", error);
      }
    }
  }
  return false;
}
function InitialContentPlugin(t0) {
  const $ = _c(5);
  const {
    initialContent,
    transformers
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== initialContent || $[2] !== transformers) {
    t1 = () => {
      editor.update(() => {
        const normalizedContent = normalizeDropMarkdown(initialContent);
        $convertFromMarkdownString(normalizedContent, transformers);
        const root = $getRoot();
        convertCodeNodesToFences(root);
        let needsAnotherPass = true;
        let passCount = 0;
        while (needsAnotherPass && passCount < MAX_MENTION_RECONSTRUCTION_PASSES) {
          const textNodes = root.getAllTextNodes();
          const hasUnprocessedMentions = textNodes.some(_temp);
          if (hasUnprocessedMentions) {
            break;
          }
          needsAnotherPass = processSplitMentions(textNodes);
          passCount = passCount + 1;
        }
        if (needsAnotherPass && passCount >= MAX_MENTION_RECONSTRUCTION_PASSES) {
          console.warn("Mention reconstruction reached max passes without converging");
        }
        root.selectEnd();
      });
    };
    t2 = [editor, initialContent, transformers];
    $[0] = editor;
    $[1] = initialContent;
    $[2] = transformers;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
}
function _temp(node) {
  const text = node.getTextContent();
  return /@\[\w+\]/.test(text) || /#\[[^\]]+\]/.test(text);
}
function FocusPlugin(t0) {
  const $ = _c(4);
  const {
    isApp
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== editor || $[1] !== isApp) {
    t1 = () => {
      const focusEditor = () => {
        editor.focus();
        requestAnimationFrame(_temp2);
      };
      if (isApp) {
        const timeouts = [setTimeout(focusEditor, 100), setTimeout(focusEditor, 350), setTimeout(focusEditor, 600)];
        return () => timeouts.forEach(clearTimeout);
      } else {
        focusEditor();
      }
    };
    t2 = [editor, isApp];
    $[0] = editor;
    $[1] = isApp;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {
  const contentEditable = document.querySelector("[contenteditable=\"true\"]") as HTMLElement;
  if (contentEditable && document.activeElement !== contentEditable) {
    contentEditable.focus();
    contentEditable.click();
  }
}
function KeyboardPlugin(t0) {
  const $ = _c(27);
  const {
    onSave,
    onCancel,
    isSaving,
    isMobileOrApp,
    initialContent,
    initialGroupMentions,
    canResolveAllGroupMention,
    transformers,
    mentionsRef,
    waveMentionsRef
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  if ($[0] !== initialContent) {
    t1 = removeBlankLinePlaceholders(initialContent);
    $[0] = initialContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sanitizedInitialContent = t1;
  let t2;
  if ($[2] !== canResolveAllGroupMention || $[3] !== editor || $[4] !== initialGroupMentions || $[5] !== isMobileOrApp || $[6] !== isSaving || $[7] !== mentionsRef || $[8] !== onCancel || $[9] !== onSave || $[10] !== sanitizedInitialContent || $[11] !== transformers || $[12] !== waveMentionsRef) {
    t2 = () => {
      const removeEscapeListener = editor.registerCommand(KEY_ESCAPE_COMMAND, () => {
        onCancel();
        return true;
      }, COMMAND_PRIORITY_HIGH);
      const removeEnterListener = editor.registerCommand(KEY_ENTER_COMMAND, event => {
        if (mentionsRef.current?.isMentionsOpen() || waveMentionsRef.current?.isWaveMentionsOpen()) {
          return false;
        }
        if (isMobileOrApp) {
          return true;
        }
        if (event?.shiftKey) {
          return false;
        }
        if (!isSaving) {
          const currentMarkdown = exportDropMarkdown(editor.getEditorState(), transformers);
          const sanitizedCurrentMarkdown = removeBlankLinePlaceholders(currentMarkdown);
          const currentMentionedGroups = getMentionedGroupsFromEditorState(editor.getEditorState(), canResolveAllGroupMention);
          if (sanitizedCurrentMarkdown.trim() === sanitizedInitialContent.trim() && areMentionedGroupsEqual(currentMentionedGroups, initialGroupMentions)) {
            onCancel();
          } else {
            onSave();
          }
        }
        return true;
      }, COMMAND_PRIORITY_HIGH);
      return () => {
        removeEscapeListener();
        removeEnterListener();
      };
    };
    $[2] = canResolveAllGroupMention;
    $[3] = editor;
    $[4] = initialGroupMentions;
    $[5] = isMobileOrApp;
    $[6] = isSaving;
    $[7] = mentionsRef;
    $[8] = onCancel;
    $[9] = onSave;
    $[10] = sanitizedInitialContent;
    $[11] = transformers;
    $[12] = waveMentionsRef;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== canResolveAllGroupMention || $[15] !== editor || $[16] !== initialContent || $[17] !== initialGroupMentions || $[18] !== isMobileOrApp || $[19] !== isSaving || $[20] !== mentionsRef || $[21] !== onCancel || $[22] !== onSave || $[23] !== sanitizedInitialContent || $[24] !== transformers || $[25] !== waveMentionsRef) {
    t3 = [editor, onSave, onCancel, isSaving, isMobileOrApp, initialContent, initialGroupMentions, canResolveAllGroupMention, transformers, mentionsRef, waveMentionsRef, sanitizedInitialContent];
    $[14] = canResolveAllGroupMention;
    $[15] = editor;
    $[16] = initialContent;
    $[17] = initialGroupMentions;
    $[18] = isMobileOrApp;
    $[19] = isSaving;
    $[20] = mentionsRef;
    $[21] = onCancel;
    $[22] = onSave;
    $[23] = sanitizedInitialContent;
    $[24] = transformers;
    $[25] = waveMentionsRef;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  useEffect(t2, t3);
  return null;
}
const EditDropLexical: React.FC<EditDropLexicalProps> = t0 => {
  const $ = _c(69);
  const {
    initialContent,
    initialMentions,
    initialGroupMentions,
    initialWaveMentions,
    canMentionAll,
    waveId,
    isSaving,
    onSave,
    onCancel
  } = t0;
  const [editorState, setEditorState] = useState(null);
  const [mentionedUsers, setMentionedUsers] = useState(initialMentions);
  const [mentionedWaves, setMentionedWaves] = useState(initialWaveMentions);
  const editorRef = useRef(null);
  const mentionsRef = useRef(null);
  const waveMentionsRef = useRef(null);
  const {
    isApp,
    isMobileDevice
  } = useDeviceInfo();
  const isMobileOrApp = isMobileDevice || isApp;
  let t1;
  if ($[0] !== initialContent) {
    t1 = normalizeDropMarkdown(initialContent);
    $[0] = initialContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedInitialContent = t1;
  let t2;
  if ($[2] !== normalizedInitialContent) {
    t2 = addBlankLinePlaceholders(normalizedInitialContent);
    $[2] = normalizedInitialContent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const editorInitialContent = t2;
  let t3;
  if ($[4] !== initialGroupMentions) {
    t3 = initialGroupMentions.includes(ApiDropGroupMention.All);
    $[4] = initialGroupMentions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasInitialAllGroupMention = t3;
  const canResolveAllGroupMention = canMentionAll || hasInitialAllGroupMention;
  let t4;
  if ($[6] !== hasInitialAllGroupMention) {
    t4 = hasInitialAllGroupMention ? [...BASE_EDIT_MARKDOWN_TRANSFORMERS, GROUP_MENTION_TRANSFORMER] : BASE_EDIT_MARKDOWN_TRANSFORMERS;
    $[6] = hasInitialAllGroupMention;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const importMarkdownTransformers = t4;
  let t5;
  if ($[8] !== canResolveAllGroupMention) {
    t5 = canResolveAllGroupMention ? [...BASE_EDIT_MARKDOWN_TRANSFORMERS, GROUP_MENTION_TRANSFORMER] : BASE_EDIT_MARKDOWN_TRANSFORMERS;
    $[8] = canResolveAllGroupMention;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const exportMarkdownTransformers = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      namespace: "EditDropLexical",
      theme: ExampleTheme,
      onError: _temp3,
      nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, HorizontalRuleNode, AutoLinkNode, LinkNode, MentionNode, GroupMentionNode, HashtagNode, WaveMentionNode, EmojiNode]
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const initialConfig = t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = editorState_0 => {
      setEditorState(editorState_0);
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleEditorChange = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = user => {
      const newMention = {
        mentioned_profile_id: user.mentioned_profile_id,
        handle_in_content: user.handle_in_content
      };
      setMentionedUsers(prev => {
        if (prev.some(m => m.mentioned_profile_id === newMention.mentioned_profile_id)) {
          return prev;
        }
        return [...prev, newMention];
      });
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleMentionSelect = t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = wave => {
      const newMention_0 = {
        wave_id: wave.wave_id,
        wave_name_in_content: wave.wave_name_in_content
      };
      setMentionedWaves(prev_0 => {
        if (prev_0.some(m_0 => m_0.wave_id === newMention_0.wave_id)) {
          return prev_0;
        }
        return [...prev_0, newMention_0];
      });
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const handleWaveMentionSelect = t9;
  let t10;
  if ($[14] !== canResolveAllGroupMention || $[15] !== editorState || $[16] !== exportMarkdownTransformers || $[17] !== initialGroupMentions || $[18] !== mentionedUsers || $[19] !== mentionedWaves || $[20] !== normalizedInitialContent || $[21] !== onCancel || $[22] !== onSave) {
    t10 = () => {
      if (!editorState) {
        return;
      }
      const markdown = exportDropMarkdown(editorState, exportMarkdownTransformers);
      const sanitizedMarkdown = removeBlankLinePlaceholders(markdown);
      const sanitizedMentionedGroups = getMentionedGroupsFromEditorState(editorState, canResolveAllGroupMention);
      if (sanitizedMarkdown.trim() === normalizedInitialContent.trim() && areMentionedGroupsEqual(sanitizedMentionedGroups, initialGroupMentions)) {
        onCancel();
        return;
      }
      onSave(sanitizedMarkdown, mentionedUsers, sanitizedMentionedGroups, mentionedWaves);
    };
    $[14] = canResolveAllGroupMention;
    $[15] = editorState;
    $[16] = exportMarkdownTransformers;
    $[17] = initialGroupMentions;
    $[18] = mentionedUsers;
    $[19] = mentionedWaves;
    $[20] = normalizedInitialContent;
    $[21] = onCancel;
    $[22] = onSave;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const handleSave = t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <RichTextPlugin contentEditable={<div className="tw-relative"><ContentEditable className="tw-min-h-[40px] tw-w-full tw-resize-none tw-overflow-y-auto tw-overflow-x-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-py-2.5 tw-pl-3 tw-pr-10 tw-text-sm tw-text-iron-100 tw-outline-none tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 focus:tw-border-primary-400 desktop-hover:hover:tw-scrollbar-thumb-iron-300" style={{
        fontFamily: "inherit",
        lineHeight: "1.4"
      }} /><CreateDropEmojiPicker top="tw-top-1" /></div>} placeholder={<div className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-2 tw-text-sm tw-text-iron-500">Edit message...</div>} ErrorBoundary={LexicalErrorBoundary} />;
    t12 = <OnChangePlugin onChange={handleEditorChange} />;
    t13 = <HistoryPlugin />;
    t14 = <PlainTextPastePlugin />;
    t15 = <MarkdownShortcutPlugin transformers={BASE_EDIT_MARKDOWN_TRANSFORMERS} />;
    t16 = <ListPlugin />;
    t17 = <LinkPlugin />;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
    t15 = $[28];
    t16 = $[29];
    t17 = $[30];
  }
  let t18;
  if ($[31] !== canMentionAll || $[32] !== waveId) {
    t18 = <NewMentionsPlugin ref={mentionsRef} waveId={waveId} onSelect={handleMentionSelect} canMentionAll={canMentionAll} />;
    $[31] = canMentionAll;
    $[32] = waveId;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <NewWaveMentionsPlugin ref={waveMentionsRef} onSelect={handleWaveMentionSelect} />;
    t20 = <EmojiPlugin />;
    $[34] = t19;
    $[35] = t20;
  } else {
    t19 = $[34];
    t20 = $[35];
  }
  let t21;
  if ($[36] !== editorInitialContent || $[37] !== importMarkdownTransformers) {
    t21 = <InitialContentPlugin initialContent={editorInitialContent} transformers={importMarkdownTransformers} />;
    $[36] = editorInitialContent;
    $[37] = importMarkdownTransformers;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== isApp) {
    t22 = <FocusPlugin isApp={isApp} />;
    $[39] = isApp;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== canResolveAllGroupMention || $[42] !== exportMarkdownTransformers || $[43] !== handleSave || $[44] !== initialGroupMentions || $[45] !== isMobileOrApp || $[46] !== isSaving || $[47] !== normalizedInitialContent || $[48] !== onCancel) {
    t23 = <KeyboardPlugin onSave={handleSave} onCancel={onCancel} isSaving={isSaving} isMobileOrApp={isMobileOrApp} initialContent={normalizedInitialContent} initialGroupMentions={initialGroupMentions} canResolveAllGroupMention={canResolveAllGroupMention} transformers={exportMarkdownTransformers} mentionsRef={mentionsRef} waveMentionsRef={waveMentionsRef} />;
    $[41] = canResolveAllGroupMention;
    $[42] = exportMarkdownTransformers;
    $[43] = handleSave;
    $[44] = initialGroupMentions;
    $[45] = isMobileOrApp;
    $[46] = isSaving;
    $[47] = normalizedInitialContent;
    $[48] = onCancel;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t18 || $[51] !== t21 || $[52] !== t22 || $[53] !== t23) {
    t24 = <LexicalComposer initialConfig={initialConfig}><div ref={editorRef} className="tw-relative">{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}</div></LexicalComposer>;
    $[50] = t18;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== handleSave || $[56] !== isApp || $[57] !== isMobileDevice || $[58] !== onCancel) {
    t25 = !isApp && <div className="tw-mt-1 tw-flex tw-items-center tw-text-xs tw-text-iron-400">{!isMobileDevice && <>escape to </>}<button onClick={onCancel} className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-[3px] tw-font-medium tw-text-primary-400 tw-transition focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 desktop-hover:hover:tw-underline">cancel</button>{" "}{isMobileDevice ? "\u2022 " : "\u2022 enter to "}<button onClick={handleSave} className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-[3px] tw-font-medium tw-text-primary-400 tw-transition focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 desktop-hover:hover:tw-underline">save</button></div>;
    $[55] = handleSave;
    $[56] = isApp;
    $[57] = isMobileDevice;
    $[58] = onCancel;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== handleSave || $[61] !== isApp || $[62] !== isSaving || $[63] !== onCancel) {
    t26 = isApp && <div className="tw-mb-2 tw-mt-3"><div className="tw-flex tw-gap-x-2"><button onClick={onCancel} disabled={isSaving} className="tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-iron-300 tw-transition-colors tw-duration-150 active:tw-bg-iron-700 disabled:tw-opacity-50">Cancel</button><button onClick={handleSave} disabled={isSaving} className="tw-rounded-lg tw-border-0 tw-bg-primary-500 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-transition-colors tw-duration-150 active:tw-bg-primary-600 disabled:tw-opacity-50">{isSaving ? "Saving..." : "Save"}</button></div></div>;
    $[60] = handleSave;
    $[61] = isApp;
    $[62] = isSaving;
    $[63] = onCancel;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== t24 || $[66] !== t25 || $[67] !== t26) {
    t27 = <div className="tw-w-full" data-editor-mode="true">{t24}{t25}{t26}</div>;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  return t27;
};
export default EditDropLexical;
function _temp3(error) {
  console.error(error);
}